import {
  SlidersHorizontal,
  User,
  Shield,
  Printer,
  Languages,
  Settings2,
  Users,
  Recycle,
  FileText,
  Warehouse,
  Database,
  TrendingUp,
  Timer,
  BookOpen,
  Tag,
  Euro,
  Palette,
  Boxes,
  Wrench,
  SlidersVertical,
} from "lucide-react";

export type SettingsEntry = {
  id: string;
  /** i18n key inside the "settings" namespace */
  titleKey: string;
  /** id of the parent entry, when this is a sub-page or an in-page section */
  parentId?: string;
  /** full target, may include ?tab= or #hash */
  path: string;
  icon: typeof User;
  keywords: string[];
  adminOnly?: boolean;
  /** true for the 12 items rendered in the normal (non-searching) menu */
  topLevel?: boolean;
  group: "personal" | "workstation" | "company";
};

export const settingsEntries: SettingsEntry[] = [
  // ---------- Personal ----------
  {
    id: "preferences",
    titleKey: "nav.preferences",
    path: "/settings/preferences",
    icon: SlidersHorizontal,
    keywords: ["general", "options", "defaults", "language", "locale", "translation", "taal", "sprache"],
    topLevel: true,
    group: "personal",
  },
  {
    id: "profile",
    titleKey: "nav.profile",
    path: "/settings/profile",
    icon: User,
    keywords: ["account", "email", "name", "avatar"],
    topLevel: true,
    group: "personal",
  },
  {
    id: "security",
    titleKey: "nav.security",
    path: "/settings/security",
    icon: Shield,
    keywords: ["password", "access", "login", "sessions"],
    topLevel: true,
    group: "personal",
  },
  {
    id: "labelPrinting",
    titleKey: "nav.labelPrinting",
    path: "/settings/label-printing",
    icon: Printer,
    keywords: ["printer", "zebra", "qz", "labels", "print", "zpl"],
    topLevel: true,
    group: "workstation",
  },

  // ---------- Company ----------
  {
    id: "companySettings",
    titleKey: "nav.companySettings",
    path: "/settings/company",
    icon: Settings2,
    keywords: ["tenant", "overrides", "company"],
    topLevel: true,
    adminOnly: true,
    group: "company",
  },
  {
    id: "companyHourlyRate",
    titleKey: "entries.hourlyRate",
    parentId: "companySettings",
    path: "/settings/company#hourly-rate",
    icon: Euro,
    keywords: ["labour", "labor", "rate", "cost", "hour", "pricing"],
    adminOnly: true,
    group: "company",
  },
  {
    id: "companyBranding",
    titleKey: "entries.branding",
    parentId: "companySettings",
    path: "/settings/company#branding",
    icon: Palette,
    keywords: ["logo", "colour", "color", "tagline", "brand", "theme"],
    adminOnly: true,
    group: "company",
  },
  {
    id: "companyCategories",
    titleKey: "entries.applianceCategoriesCompany",
    parentId: "companySettings",
    path: "/settings/company#appliance-categories",
    icon: Boxes,
    keywords: ["categories", "appliance", "types", "enabled"],
    adminOnly: true,
    group: "company",
  },
  {
    id: "companyLabelPrinting",
    titleKey: "entries.labelPrintingCompany",
    parentId: "companySettings",
    path: "/settings/company#label-printing",
    icon: Printer,
    keywords: ["zebra", "qz tray", "zpl", "certificate", "label", "printer"],
    adminOnly: true,
    group: "company",
  },
  {
    id: "gradePricing",
    titleKey: "entries.gradePricing",
    parentId: "partsHarvesting",
    path: "/settings/parts-harvesting#grade-pricing",
    icon: Euro,
    keywords: ["refurbishment", "grade", "a", "b", "c", "retail", "price"],
    adminOnly: true,
    group: "company",
  },
  {
    id: "companyLanguages",
    titleKey: "entries.companyLanguages",
    parentId: "companySettings",
    path: "/settings/company/languages",
    icon: Languages,
    keywords: ["locales", "translations", "enabled languages"],
    adminOnly: true,
    group: "company",
  },

  {
    id: "users",
    titleKey: "nav.users",
    path: "/settings/users",
    icon: Users,
    keywords: ["roles", "permissions", "members", "team"],
    topLevel: true,
    adminOnly: true,
    group: "company",
  },
  {
    id: "usersTab",
    titleKey: "entries.usersList",
    parentId: "users",
    path: "/settings/users?tab=users",
    icon: Users,
    keywords: ["invite", "member", "account", "people"],
    adminOnly: true,
    group: "company",
  },
  {
    id: "rolesTab",
    titleKey: "entries.roles",
    parentId: "users",
    path: "/settings/users?tab=roles",
    icon: Tag,
    keywords: ["permissions", "custom role", "superadmin", "access"],
    adminOnly: true,
    group: "company",
  },

  {
    id: "partsHarvesting",
    titleKey: "nav.partsHarvesting",
    path: "/settings/parts-harvesting",
    icon: Recycle,
    keywords: ["harvest", "parts", "components", "mapping", "brand", "spare parts", "grade", "pricing"],
    topLevel: true,
    adminOnly: true,
    group: "company",
  },
  {
    id: "harvestThresholds",
    titleKey: "entries.harvestThresholds",
    parentId: "partsHarvesting",
    path: "/settings/parts-harvesting#harvest-thresholds",
    icon: SlidersVertical,
    keywords: ["min demand", "min price", "priority", "resale", "recommendation"],
    adminOnly: true,
    group: "company",
  },
  {
    id: "harvestComponents",
    titleKey: "entries.harvestComponents",
    parentId: "partsHarvesting",
    path: "/settings/parts-harvesting?tab=components",
    icon: Wrench,
    keywords: ["components", "harvestable", "part list"],
    adminOnly: true,
    group: "company",
  },
  {
    id: "harvestBrandMapping",
    titleKey: "entries.harvestBrandMapping",
    parentId: "partsHarvesting",
    path: "/settings/parts-harvesting?tab=mapping",
    icon: Tag,
    keywords: ["brand", "mapping", "aliases"],
    adminOnly: true,
    group: "company",
  },

  {
    id: "triageRules",
    titleKey: "nav.triageRules",
    path: "/settings/triage-rules",
    icon: FileText,
    keywords: ["rules", "device", "triage", "rulesets", "pre-triage", "scanner"],
    topLevel: true,
    adminOnly: true,
    group: "company",
  },
  {
    id: "storage",
    titleKey: "nav.storage",
    path: "/settings/storage",
    icon: Warehouse,
    keywords: ["bins", "warehouse", "locations", "labels", "areas"],
    topLevel: true,
    adminOnly: true,
    group: "company",
  },

  {
    id: "data",
    titleKey: "nav.data",
    path: "/settings/data",
    icon: Database,
    keywords: ["phase times", "analytics", "guides", "manuals"],
    topLevel: true,
    adminOnly: true,
    group: "company",
  },
  {
    id: "phaseTimes",
    titleKey: "entries.phaseTimes",
    parentId: "data",
    path: "/settings/data/phase-times",
    icon: Timer,
    keywords: ["phase", "time", "estimates", "duration", "minutes", "analytics"],
    adminOnly: true,
    group: "company",
  },
  {
    id: "repairGuides",
    titleKey: "entries.repairGuides",
    parentId: "data",
    path: "/settings/data/repair-guides",
    icon: BookOpen,
    keywords: ["manuals", "guides", "exploded views", "documents", "pdf"],
    adminOnly: true,
    group: "company",
  },
];

export const topLevelPersonal = settingsEntries.filter((e) => e.topLevel && e.group === "personal");
export const topLevelWorkstation = settingsEntries.filter((e) => e.topLevel && e.group === "workstation");
export const topLevelCompany = settingsEntries.filter((e) => e.topLevel && e.group === "company");

export function findEntry(id: string) {
  return settingsEntries.find((e) => e.id === id);
}
